import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useEscapeLayer } from '@/components/escapeLayer';
import { HaiIcon } from '@/components/icons';
import { useSwitchGroup } from '@/hooks/useSwitchGroup';
import { useGroupContextStore } from '@/stores/groupContextStore';
import { isContractWorkspace, isDefaultTrialWorkspace, type GroupEntity } from '@/types';

/**
 * GroupSwitcher(R45 / Beta v0.0.1 Phase 4 → R48.10 visibility pivot → R73 billing)
 * -----------------------------------------------------------------------------
 * TopNav 右側 dropdown chip,顯示當前 group + 切換到其他 group。
 *
 * **R48.10 — Bryan UX pivot**:原 R45 設計「`availableGroups.length <= 1` 隱藏」
 * 對 personal-only user 完全沒入口切 group。現改成「**永遠顯示**」即使單 group
 * 也露 chip(顯「個人」標)。0 group 時顯「— 載入中」(degraded);1+ group 時
 * dropdown 列出全部。
 *
 * 點按開 dropdown 顯示:
 * - 「個人」/ group name list,當前 group 加勾(HaiIcon `ck`)
 * - 點選 → `useSwitchGroup(gid)` orchestrate detach + clear + navigate `/projects`
 * - 「工作區設定」(`gear` 圖示;常駐,對齊 M-00 dropdown footer)→ `/workspace/settings`
 * - 「成員管理」(`user` 圖示;非 personal group 才出現)→ `/groups/:gid/members`
 * - R393 CIS:點外面與 ESC 都能收起(ESC 走 `useEscapeLayer` 分層)
 *
 * **沒有「建立新工作區」**(2026-08-10 拿掉;理由見下方 footer actions 註解):
 * 合約制下工作區由管理員建合約開通,BE 的自助建立端點早已移除。
 *
 * **R73 billing 整合(docs/46 §5.1 / §6)**:
 * - option / 當前 chip 就地派生灰 badge:`PENDING_ORDER`「待付款」/ `SUSPENDED`
 *   「已停用」/ 試玩(`isDefaultTrialWorkspace`)「試玩」;status badge 優先於試玩
 * - 待付款 option 點擊**不走 switchGroup** → `/billing/orders`(完成付款入口);
 *   `useSwitchGroup` 守門 reject(WORKSPACE_NOT_SWITCHABLE)同樣導 `/billing/orders`
 * - 已停用 option 點擊不切(重新開通本期無 endpoint,只擋 + title hint)
 */

/**
 * dropdown option / 當前 chip 的工作區狀態 badge(null = 不掛 badge)。
 * R105 — 金流移除(docs/64):`pendingOrder`(待付款,billing 訂單概念)拿掉;合約制無訂單。
 * 保留 `suspended`(合約停用)/ `trial`(試玩派生)。
 */
type GroupBadge = 'suspended' | 'trial' | null;

/**
 * 切換器 chevron(D3 `專案首頁.html` `.ws-swap`)— trigger 右側下箭頭;`open` 時旋 180°。
 * R393 — 收斂到 HaiIcon 單一來源(`swap` = D3 ws-swap 15×15 原 path;不再自繪)。
 */
function Chevron({ open }: { open: boolean }) {
  return (
    <HaiIcon
      name="swap"
      style={{
        color: 'var(--ink-500)',
        flexShrink: 0,
        transition: 'transform 150ms ease',
        transform: open ? 'rotate(180deg)' : 'none',
      }}
    />
  );
}

/**
 * 漸層 logo 方塊(M-00 line 119-127)— 取 group 顯示名首字白字 + 漸層底。
 * R382(owner 2026-09-24:「工作區使用灰階色系,因為整體服務還沒有用到最好」)—
 * 頭像改**灰階**(`--ink-*`,兩主題自動翻),原品牌橘漸層(`--logo-grad-*`)只留給
 * app logo 本體 —— 工作區功能未成熟前不用品牌色搶視覺。全 group 仍共用同一款
 * (不依名稱換色 — per-group 配色 BE 未提供,不偽造)。`size` 控制 trigger / row。
 */
function LogoSquare({ label, size }: { label: string; size: number }) {
  const initial = label.trim().charAt(0).toUpperCase() || '·';
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 'var(--r-xs)',
        background: 'var(--ink-800)',
        border: '1px solid var(--hairline)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--ink-300)',
        fontWeight: 700,
        // D3 `.ws-logo` 13px / `.ws-logo.sm`(28px)12px —— CIS 最小字級 10.5,禁 10
        fontSize: size >= 32 ? 13 : 12,
        letterSpacing: '-0.02em',
        boxSizing: 'border-box',
      }}
    >
      {initial}
    </span>
  );
}

/**
 * Badge 派生:status badge(影響可否切換)優先於試玩 chip。
 * `PENDING_ORDER` / `SUSPENDED` 比對是 enum 其餘兩值 — 「禁裸比對」紅線只鎖
 * `=== 'ACTIVE'`(走 `isWorkspaceActive` selector);此處不判 active。
 */
function badgeFor(g: GroupEntity): GroupBadge {
  if (g.status === 'SUSPENDED') return 'suspended';
  if (isDefaultTrialWorkspace(g)) return 'trial';
  return null;
}

/**
 * billing.json key tree 由凍結段落提供(A2 平行重寫中);落地前動態 key 走
 * loose cast(對齊 repo「HomeSidebar LooseT」慣例)— key 一律取自凍結表
 * (`badge.*` / `banner.trialCta`)。
 */
type LooseT = (key: string, opts?: Record<string, unknown>) => string;

/** 灰 badge chip(試玩 / 待付款 / 已停用 — 中性視覺,預期狀態不用紅色)。 */
function BadgeChip({ label }: { label: string }) {
  return (
    <span data-testid="group-badge" style={badgeChipStyle}>
      {label}
    </span>
  );
}

const badgeChipStyle: CSSProperties = {
  padding: '1px 6px',
  fontSize: 'var(--text-xs)',
  color: 'var(--ink-500)',
  background: 'var(--ink-800)',
  border: '1px solid var(--hairline)',
  borderRadius: 'var(--r-xs)',
  whiteSpace: 'nowrap',
};

/** Uppercase section header(D3 `.ws-sec`:10.5 / 700 / .06em / `#5B697F`)。 */
function SectionHeader({ label, borderTop }: { label: string; borderTop?: boolean }) {
  return (
    <div
      style={{
        padding: '12px 16px 8px',
        fontSize: 10.5,
        textTransform: 'uppercase',
        letterSpacing: '.06em',
        color: 'var(--ink-500)',
        fontWeight: 700,
        borderTop: borderTop ? '1px solid var(--hairline)' : undefined,
      }}
    >
      {label}
    </div>
  );
}

interface GroupRowProps {
  group: GroupEntity;
  selected: boolean;
  badge: GroupBadge;
  badgeLabel: (b: Exclude<GroupBadge, null>) => string;
  personalLabel: string;
  subtitleFor: (g: GroupEntity) => string | null;
  onSelect: (g: GroupEntity) => void;
}

/**
 * 工作區列(M-00 line 154-161 current / 167-172 other)— 36×36 logo + 名稱 +
 * 副標(成員數;sans + tabular-nums)+ 當前列 HaiIcon `ck` 勾。current 列底色 teal-50
 * (`--concept-soft`,D3 `.ws-row.current`),hover slate-50。
 * 保留 billing badge + suspended disabled(不整列淡化)+ title hint。
 */
function GroupRow({
  group,
  selected,
  badge,
  badgeLabel,
  personalLabel,
  subtitleFor,
  onSelect,
}: GroupRowProps) {
  const { t } = useTranslation('groups');
  const name = group.isPersonal ? personalLabel : group.name;
  const subtitle = subtitleFor(group);
  // 已停用 hint;ACTIVE / 試玩無 title(R105:待付款狀態隨金流移除)
  const title = badge === 'suspended' ? t('switcher.suspendedHint') : undefined;
  const disabled = badge === 'suspended';

  return (
    <button
      role="option"
      aria-selected={selected}
      aria-disabled={disabled || undefined}
      data-testid={`group-option-${group.gid}`}
      type="button"
      title={title}
      onClick={() => onSelect(group)}
      onMouseEnter={(e) => {
        if (!selected && !disabled) e.currentTarget.style.background = 'var(--slate-50)';
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.background = 'transparent';
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '10px 12px',
        // D3 `.ws-row.current` 底 #F0FDFA = teal-50(CIS 選取態;藍只給成品,舊 M-00 #EFF6FF 已過時)
        background: selected ? 'var(--concept-soft)' : 'transparent',
        border: 'none',
        borderRadius: 'var(--r-sm)',
        // 已停用:不整列 opacity 淡化(CIS 停用但仍需閱讀的文字走次文字色),靠 cursor + title + badge
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
        transition: 'background 120ms ease',
      }}
    >
      <LogoSquare label={name} size={36} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            minWidth: 0,
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: selected ? 600 : 500,
              // D3 `.ws-row.current .ws-name` = #134E4A(teal-900);停用列 = 次文字
              color: selected ? 'var(--teal-900)' : disabled ? 'var(--ink-500)' : 'var(--ink-200)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </span>
          {badge && <BadgeChip label={badgeLabel(badge)} />}
        </span>
        {subtitle && (
          <span
            style={{
              display: 'block',
              // 「N 位成員」是一般文字 + 數字 → sans + tabular-nums(CIS:mono 只給 ID / 網域 / 代碼)
              fontVariantNumeric: 'tabular-nums',
              fontSize: 10.5,
              color: 'var(--ink-500)',
              marginTop: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </span>
        )}
      </span>
      {selected && (
        <HaiIcon name="ck" size={14} style={{ color: 'var(--tide-600)', flexShrink: 0 }} />
      )}
    </button>
  );
}

/** 底部 action 列共用樣式(M-00 line 192-203)。 */
const footerActionStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '8px 12px',
  fontSize: 13,
  color: 'var(--ink-200)',
  background: 'transparent',
  border: 'none',
  borderRadius: 'var(--r-sm)',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background 120ms ease',
};

const solidIconBoxStyle: CSSProperties = {
  width: 36,
  height: 36,
  flexShrink: 0,
  borderRadius: 'var(--r-sm)',
  background: 'var(--slate-100)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--ink-500)',
};

function hoverOn(e: ReactMouseEvent<HTMLButtonElement>) {
  e.currentTarget.style.background = 'var(--slate-50)';
}
function hoverOff(e: ReactMouseEvent<HTMLButtonElement>) {
  e.currentTarget.style.background = 'transparent';
}

export interface GroupSwitcherProps {
  /**
   * R164 — 側邊欄收合態:trigger 只顯 logo 方塊(置中無框),dropdown 改開右側
   * (D3 `aside.sidebar.collapsed .ws-dropdown{top:0;left:calc(100% + 10px)}`)。
   */
  collapsed?: boolean;
}

export function GroupSwitcher({ collapsed = false }: GroupSwitcherProps) {
  const { t } = useTranslation('groups');
  // D6 dropdown 新 section header「切換到其他工作區」key `switcher.otherSection` 由
  // foundation 補進 groups.json(尚未凍結)→ 走 loose cast(對齊本檔既有 LooseT 慣例:
  // 動態 / 待凍結 key 不撞 resources.d.ts 嚴格型別)。key 加入後可改回 strict `t`。
  const ltGroups = t as unknown as LooseT;
  const groups = useGroupContextStore((s) => s.availableGroups);
  const currentGroupId = useGroupContextStore((s) => s.currentGroupId);
  const switchGroup = useSwitchGroup();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click outside 收 dropdown
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  // CIS 下拉規範:點外面與 ESC 皆可關(ESC 走全站分層,只收最上層)
  useEscapeLayer(() => setOpen(false), open);

  // R48.10 — 永遠顯示 chip(personal-only user 也要有入口建/切 group)
  const current = groups.find((g) => g.gid === currentGroupId);
  const label = current
    ? current.isPersonal
      ? t('switcher.personal')
      : current.name
    : groups.length === 0
      ? t('switcher.loading')
      : t('switcher.label');

  // 當前 group 的狀態 badge(trigger chip 同步顯示)
  const currentBadge = current ? badgeFor(current) : null;
  const badgeLabel = (b: Exclude<GroupBadge, null>): string =>
    b === 'suspended' ? t('switcher.badgeSuspended') : t('switcher.badgeTrial');

  // D6 dropdown 分兩段:目前 vs 其他(M-00 line 152 / 165)
  const others = groups.filter((g) => g.gid !== currentGroupId);

  // mono 副標(M-00 line 158「denimco.com · 3 個廣告帳戶」)。BE 未提供 domain /
  // 廣告帳戶數 → 走誠實真值 memberCount(沿用 members.memberCount i18n key);
  // 不偽造 domain。memberCount 缺(0)時不顯副標。
  const subtitleFor = (g: GroupEntity): string | null =>
    g.memberCount > 0 ? t('members.memberCount', { count: g.memberCount }) : null;

  /**
   * 選 group(同舊 onClick 分流邏輯,抽出供 current / others 兩段 row 共用):
   * suspended 不切不導頁 / pendingOrder 導訂單頁 / 其餘走 switchGroup。
   */
  const handleSelect = (g: GroupEntity): void => {
    const badge = badgeFor(g);
    // 已停用:不切不導頁(合約制重新開通無自助 endpoint,只擋;dropdown 留著看 title hint)
    if (badge === 'suspended') return;
    setOpen(false);
    if (g.gid !== currentGroupId) {
      // useSwitchGroup 改 async(PATCH /me → re-fetch);catch 防 unhandled rejection。
      // R105 — 守門 reject(非 ACTIVE)無金流頁可導向(合約制)→ 僅 log warn(SUSPENDED
      // 已在上方 early-return 擋下,此 catch 為防禦)。
      void switchGroup(g.gid).catch((err) => {
        console.warn('[group-switcher] switchGroup failed', err);
      });
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* R164 側邊欄 trigger(D3 .sb-ws .ws-trigger):整寬 + border 圓角 12,
          內容 = 28px 漸層 logo + 「工作區」kicker / 名稱直排 + chevron;
          collapsed 只顯 logo 置中無框(hover 才起底色)。 */}
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('switcher.label')}
        data-testid="group-switcher-trigger"
        onMouseEnter={(e) => {
          if (collapsed) {
            e.currentTarget.style.background = 'var(--slate-100)';
          } else {
            e.currentTarget.style.borderColor = 'var(--tide-200)';
            e.currentTarget.style.background = 'var(--slate-50)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          if (!collapsed) e.currentTarget.style.borderColor = 'var(--hairline)';
        }}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: collapsed ? '8px 0' : '8px 10px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          fontSize: 'var(--text-sm)',
          color: 'var(--ink-200)',
          background: 'transparent',
          border: collapsed ? '1px solid transparent' : '1px solid var(--hairline)',
          borderRadius: collapsed ? 11 : 12,
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'background 120ms ease, border-color 120ms ease',
        }}
      >
        <LogoSquare label={current ? label : 'H'} size={28} />
        {!collapsed && (
          <>
            <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <span
                style={{
                  fontSize: 10.5,
                  color: 'var(--ink-500)',
                  fontWeight: 500,
                  lineHeight: 1.25,
                }}
              >
                {t('switcher.workspaceKicker')}
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--slate-800)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {label}
                {currentBadge && <BadgeChip label={badgeLabel(currentBadge)} />}
              </span>
            </span>
            <Chevron open={open} />
          </>
        )}
      </button>
      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            // collapsed:開右側(D3 collapsed .ws-dropdown override);展開:開下方。
            ...(collapsed
              ? { top: 0, left: 'calc(100% + 10px)' }
              : { top: 'calc(100% + 8px)', left: 0 }),
            width: 300,
            background: 'var(--ink-900)',
            border: '1px solid var(--hairline)',
            borderRadius: 14,
            boxShadow: 'var(--shadow-lg)',
            zIndex: 70,
            overflow: 'hidden',
          }}
        >
          {/* 目前工作區 section(M-00 line 152-162)*/}
          {current && (
            <>
              <SectionHeader label={t('switcher.label')} />
              <div style={{ padding: '0 8px 8px' }}>
                <GroupRow
                  group={current}
                  selected
                  badge={badgeFor(current)}
                  badgeLabel={badgeLabel}
                  personalLabel={t('switcher.personal')}
                  subtitleFor={subtitleFor}
                  onSelect={handleSelect}
                />
              </div>
            </>
          )}

          {/* 切換到其他工作區 section(M-00 line 165-188)*/}
          {others.length > 0 && (
            <>
              <SectionHeader
                label={ltGroups('switcher.otherSection')}
                borderTop
              />
              <div style={{ padding: '0 8px 8px', maxHeight: 256, overflowY: 'auto' }}>
                {others.map((g) => (
                  <GroupRow
                    key={g.gid}
                    group={g}
                    selected={false}
                    badge={badgeFor(g)}
                    badgeLabel={badgeLabel}
                    personalLabel={t('switcher.personal')}
                    subtitleFor={subtitleFor}
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            </>
          )}

          {/* 底部 actions — 工作區設定(齒輪)/ 成員管理 */}
          <div style={{ borderTop: '1px solid var(--hairline)', padding: 8 }}>
            {/* 「建立新工作區」整條退場(Bryan 2026-08-10「個人用戶並沒有新增工作區的能力」):
                BE `POST /api/groups` 已於 **2026-06-23 移除**(`groups/router.py:186`,owner 拍板
                「建工作區一律走 billing checkout」),而 billing 又在 R103–R109 隨合約制拆掉 →
                這顆鈕通往一支不存在的端點,按下去只會 404。合約制下工作區開通 = 管理員建合約
                (`/admin/contracts`),不是使用者自助。`GroupCreateDialog` 與 `groupsService.create`
                同輪一併刪除(唯一 caller 就是這裡)。
                R105 的「建立付費工作區」入口更早就隨金流移除。 */}
            {/* 工作區設定(M-00 dropdown footer line 148-151 齒輪入口)— 對齊設計稿
                在 dropdown 底部常駐「工作區設定」齒輪 entry → `/workspace/settings`。
                成員管理(下方)仍只在非 personal group 顯示;此設定入口不分 group 型別。 */}
            <button
              type="button"
              data-testid="group-workspace-settings"
              onClick={() => {
                setOpen(false);
                navigate('/workspace/settings');
              }}
              style={footerActionStyle}
              onMouseEnter={hoverOn}
              onMouseLeave={hoverOff}
            >
              <span aria-hidden style={solidIconBoxStyle}>
                <HaiIcon name="gear" />
              </span>
              {t('switcher.workspaceSettings')}
            </button>
            {current && !current.isPersonal && currentGroupId && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  // R106(docs/65 §③)— 合約工作區成員走 /contracts/mine(email seat);
                  // 非合約 team group 維持舊 /groups/{gid}/members(cuid)。
                  navigate(isContractWorkspace(current) ? '/contracts/mine' : `/groups/${currentGroupId}/members`);
                }}
                style={footerActionStyle}
                onMouseEnter={hoverOn}
                onMouseLeave={hoverOff}
              >
                <span aria-hidden style={solidIconBoxStyle}>
                  {/* 成員管理 = user(原本與工作區設定畫成同一顆齒輪) */}
                  <HaiIcon name="user" size={15} />
                </span>
                {t('switcher.manageMembers')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
